import { useEffect, useLayoutEffect, useState, type CSSProperties } from "react";
import { fnv1a, frameFor, type Photo } from "../../../shared/photos";
import { useDevicePrefs } from "../../app/devicePrefs";

/** Hand-drawn photo glyph (24px stroke, same style as ui/Icon). */
export function PhotoGlyph({ size = "1.6rem" }: { size?: string | number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3.5" y="5" width="17" height="14" rx="2.5" />
      <path d="M4 16.5l4.5-4.5 4 4 2.5-2.5 5 5" />
      <circle cx="15.5" cy="9.5" r="1.4" />
    </svg>
  );
}

/** True when the OS asks for reduced motion. */
export function usePrefersReducedMotion(): boolean {
  const query = "(prefers-reduced-motion: reduce)";
  const [reduced, setReduced] = useState(() => typeof window !== "undefined" && window.matchMedia?.(query).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return;
    const on = () => setReduced(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return reduced;
}

/** Motion is off when this screen asked for lighter effects or the OS asks for reduced motion. */
export function useStillPhotos(): boolean {
  const { reduceEffects } = useDevicePrefs();
  return usePrefersReducedMotion() || reduceEffects;
}

export function usePageVisible(): boolean {
  const [visible, setVisible] = useState(() => document.visibilityState === "visible");
  useEffect(() => {
    const on = () => setVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", on);
    return () => document.removeEventListener("visibilitychange", on);
  }, []);
  return visible;
}

/** The size of an element, kept current (photos are framed to the exact box). A callback
 *  ref, so it also works for an element that appears after a loading state. */
export function useBoxSize<T extends HTMLElement>(): [(el: T | null) => void, { w: number; h: number }] {
  const [el, setEl] = useState<T | null>(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setBox((b) => (Math.abs(b.w - r.width) < 1 && Math.abs(b.h - r.height) < 1 ? b : { w: Math.round(r.width), h: Math.round(r.height) }));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return [setEl, box];
}

const THUMB_EDGE = 640;

/**
 * The thumbnail (640 px) when it's shown no bigger than it is: spares a
 * low-memory wall computer from decoding 2048 px images it would only shrink.
 */
export function variantFor(photo: Photo, box: { w: number; h: number }, motion = false): "thumb" | "full" {
  const f = frameFor(photo, box.w, box.h, { motion });
  const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
  const shown = Math.max(f.width, f.height) * dpr * f.zoom;
  return shown <= THUMB_EDGE * 1.1 ? "thumb" : "full";
}

/**
 * One photo filling its box (frame.ts decides how): filled edge to edge with the subject
 * kept in view, or shrunk over a soft blurred copy of itself, or panning across a
 * panorama. With motion, a slow zoom about the subject that never pushes it out.
 */
export function PhotoImage({
  photo,
  box,
  motion,
  durationMs,
  still,
  onLoad,
  onError,
}: {
  photo: Photo;
  box: { w: number; h: number };
  /** Slow zoom or pan over durationMs. */
  motion: boolean;
  durationMs: number;
  still: boolean;
  onLoad?: () => void;
  onError?: () => void;
}) {
  if (!box.w || !box.h) return null;
  const moving = motion && !still;
  const f = frameFor(photo, box.w, box.h, { motion: moving });
  const variant = variantFor(photo, box, moving);
  const zoomIn = (fnv1a(photo.id) & 1) === 0;
  const anim = !moving ? "" : f.mode === "pan" ? "pf-pan" : f.zoom > 1.01 ? "pf-kb" : "";
  const style: CSSProperties = {
    left: f.left,
    top: f.top,
    width: f.width,
    height: f.height,
    transformOrigin: `${(f.origin.x * 100).toFixed(1)}% ${(f.origin.y * 100).toFixed(1)}%`,
    ["--kb-s0" as string]: String(zoomIn ? 1 : f.zoom),
    ["--kb-s1" as string]: String(zoomIn ? f.zoom : 1),
    ["--pan-dx" as string]: `${f.pan?.dx ?? 0}px`,
    ["--pan-dy" as string]: `${f.pan?.dy ?? 0}px`,
    ["--kb-dur" as string]: `${durationMs}ms`,
  };
  return (
    <>
      {f.backdrop && (still ? <div className="pf-backdrop plain" /> : <img className="pf-backdrop" src={photo.thumbUrl} alt="" aria-hidden="true" decoding="async" draggable={false} />)}
      <img
        className={`pf-img framed ${anim}`}
        style={style}
        src={variant === "full" ? photo.url : photo.thumbUrl}
        alt={photo.alt ?? photo.caption ?? "Family photo"}
        decoding="async"
        draggable={false}
        onLoad={onLoad}
        onError={onError}
      />
    </>
  );
}
